/**
 * MoonUI Pro Cache Helper Utilities
 * 
 * Use these functions in browser console to manage MoonUI Pro authentication cache
 * 
 * Example usage in browser console:
 * 1. Open browser DevTools (F12)
 * 2. Go to Console tab
 * 3. Run: window.MoonUICache.clearAndRefresh()
 */

// Import functions from hook - will be available after build
let forceRefresh: any;
let clearCache: any;
let cliTokenReader: any;

// Dynamic import to avoid circular dependencies
if (typeof window !== 'undefined') {
  import('../hooks/use-subscription').then(module => {
    forceRefresh = module.forceRefresh;
    clearCache = module.clearCache;
  });
  
  import('../services/cli-token-reader').then(module => {
    cliTokenReader = module.cliTokenReader;
  });
}

// Cache management utilities
export const MoonUICache = {
  /**
   * Clear all MoonUI Pro authentication cache
   */
  clear: () => {
    clearCache();
    console.log('✅ MoonUI Pro cache cleared');
  },

  /**
   * Force refresh authentication (clear cache and revalidate)
   */
  refresh: async () => {
    console.log('🔄 Refreshing MoonUI Pro authentication...');
    await forceRefresh();
    console.log('✅ Authentication refreshed');
  },

  /**
   * Clear cache and force refresh in one step
   */
  clearAndRefresh: async () => {
    console.log('🧹 Clearing cache and refreshing...');
    clearCache();
    await forceRefresh();
    console.log('✅ Cache cleared and authentication refreshed');
  },

  /**
   * Check current cache status
   */
  status: () => {
    const cache = localStorage.getItem('moonui_license_cache');
    const token = localStorage.getItem('moonui_auth_token');
    
    if (!cache && !token) {
      console.log('❌ No authentication cache or token found');
      return null;
    }

    if (cache) {
      const cacheData = JSON.parse(cache);
      const now = Date.now();
      const age = now - cacheData.timestamp;
      const ageMinutes = Math.floor(age / 60000);
      
      console.log('📊 Cache Status:');
      console.log(`  Valid: ${cacheData.valid ? '✅' : '❌'}`);
      console.log(`  Has Lifetime Access: ${cacheData.hasLifetimeAccess ? '✅' : '❌'}`);
      console.log(`  Cache Age: ${ageMinutes} minutes`);
      console.log(`  Timestamp: ${new Date(cacheData.timestamp).toLocaleString()}`);
      
      return cacheData;
    }
    
    if (token) {
      console.log('🔑 Auth token found but no cache');
      return { token };
    }
  },

  /**
   * Debug info - shows all relevant localStorage keys
   */
  debug: async () => {
    console.log('🔍 MoonUI Pro Debug Info:');
    console.log('----------------------------');
    
    const keys = [
      'moonui_license_cache',
      'moonui_auth_token',
      'moonui-sidebar-state'
    ];
    
    keys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        console.log(`📦 ${key}:`);
        try {
          const parsed = JSON.parse(value);
          console.log(parsed);
        } catch {
          console.log(value);
        }
      } else {
        console.log(`❌ ${key}: Not found`);
      }
    });
    
    // Check CLI token in development
    if (process.env.NODE_ENV === 'development' && cliTokenReader) {
      console.log('\n🔐 CLI Authentication:');
      console.log('----------------------------');
      try {
        const cliToken = await cliTokenReader.getCLIToken();
        if (cliToken) {
          console.log('✅ CLI token found');
          console.log(`   Token: ${cliToken.substring(0, 20)}...`);
        } else {
          console.log('❌ No CLI token available');
          console.log('   Run "moonui login" to authenticate');
        }
      } catch (error) {
        console.log('❌ Failed to check CLI token:', error);
      }
    }
  },

  /**
   * Check CLI authentication status
   */
  checkCLIAuth: async () => {
    if (process.env.NODE_ENV !== 'development' || !cliTokenReader) {
      console.log('❌ CLI auth check only available in development');
      return false;
    }
    
    try {
      const token = await cliTokenReader.getCLIToken();
      if (token) {
        console.log('✅ CLI authentication active');
        return true;
      } else {
        console.log('❌ No CLI authentication found');
        console.log('💡 Run "moonui login" to authenticate');
        return false;
      }
    } catch (error) {
      console.error('❌ Failed to check CLI auth:', error);
      return false;
    }
  }
};

// Expose to window for browser console access
if (typeof window !== 'undefined') {
  (window as any).MoonUICache = MoonUICache;
  
  // Auto-clear cache if URL has ?clear-cache parameter
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('clear-cache')) {
    console.log('🔄 Auto-clearing cache due to URL parameter...');
    MoonUICache.clearAndRefresh().then(() => {
      // Remove the parameter from URL
      urlParams.delete('clear-cache');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    });
  }
}

export default MoonUICache;